import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getErrorMessage } from '@/lib/api'
import { tenantInputToIso, useRequiredTenantTimeZone } from '@/lib/tenant-date-time'
import type { AttendanceRequestType } from '@/services/workers'

export function AttendanceRequestDialog({
  open,
  onOpenChange,
  employeeName,
  workerId,
  workDate,
  currentCheckIn,
  currentCheckOut,
  isPending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeName: string
  workerId: string
  workDate: string
  currentCheckIn?: string
  currentCheckOut?: string
  isPending?: boolean
  onSubmit: (payload: {
    workerId: string
    workDate: string
    requestType: AttendanceRequestType
    requestedCheckIn?: string
    requestedCheckOut?: string
    reason?: string
  }) => Promise<unknown> | void
}) {
  const [requestType, setRequestType] = useState<AttendanceRequestType>(currentCheckOut ? 'time_correction' : 'missing_punch')
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [reason, setReason] = useState('')
  // Requested times are tenant wall-clock on the work date; never the browser zone or its offset.
  const { timeZone: tenantTimeZone, isReady: isTenantTimeZoneReady } = useRequiredTenantTimeZone()

  useEffect(() => {
    if (!open) return
    setRequestType(currentCheckOut ? 'time_correction' : 'missing_punch')
    setCheckIn('')
    setCheckOut('')
    setReason('')
  }, [currentCheckOut, open])

  const submit = async () => {
    if (!workerId.trim()) {
      toast.error('Employee is required')
      return
    }
    if (!workDate.trim()) {
      toast.error('Date is required')
      return
    }
    if (!checkIn && !checkOut) {
      toast.error('Enter a check-in time, a check-out time, or both')
      return
    }
    if (!isTenantTimeZoneReady) {
      return
    }
    try {
      await onSubmit({
        workerId,
        workDate,
        requestType,
        requestedCheckIn: checkIn ? tenantInputToIso(`${workDate}T${checkIn}`, tenantTimeZone) : undefined,
        requestedCheckOut: checkOut ? tenantInputToIso(`${workDate}T${checkOut}`, tenantTimeZone) : undefined,
        reason: reason.trim() || undefined,
      })
      onOpenChange(false)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Raise request</DialogTitle>
          <DialogDescription>Ask for a missing punch or a time correction on this time log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Employee</Label>
            <Input value={employeeName} disabled />
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={workDate} disabled />
          </div>
          <div className="space-y-2">
            <Label>Request</Label>
            <Select value={requestType} onValueChange={(value) => setRequestType(value as AttendanceRequestType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="missing_punch">Missing punch</SelectItem>
                <SelectItem value="time_correction">Time correction</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Requested check-in</Label>
              <Input type="time" value={checkIn} onChange={(event) => setCheckIn(event.target.value)} />
              {currentCheckIn ? <p className="text-xs text-slate-500">Recorded {currentCheckIn}</p> : null}
            </div>
            <div className="space-y-2">
              <Label>Requested check-out</Label>
              <Input type="time" value={checkOut} onChange={(event) => setCheckOut(event.target.value)} />
              {currentCheckOut ? <p className="text-xs text-slate-500">Recorded {currentCheckOut}</p> : null}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why this punch needs a change" />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button type="button" onClick={() => void submit()} disabled={isPending || !isTenantTimeZoneReady}>{isPending ? 'Saving...' : 'Raise request'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
